import { useState } from 'react';
import {
  Box,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Badge,
  Collapse,
  Typography,
  Button,
} from '@mui/material';
import { FilterList, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SessionFilters as SessionFiltersType, DatePreset } from '@/types/filters';
import { SessionStatus } from '@/types/session';

interface SessionFiltersProps {
  filters: SessionFiltersType;
  availableWards: string[];
  activeFilterCount: number;
  onFilterChange: <K extends keyof SessionFiltersType>(
    key: K,
    value: SessionFiltersType[K],
  ) => void;
  onClearFilters: () => void;
}

const DATE_PRESETS: { key: DatePreset; labelKey: string }[] = [
  { key: 'all', labelKey: 'filters.allDates' },
  { key: 'today', labelKey: 'filters.today' },
  { key: 'last7', labelKey: 'filters.last7' },
  { key: 'last30', labelKey: 'filters.last30' },
];

const STATUS_OPTIONS: { key: SessionStatus | 'all'; labelKey: string }[] = [
  { key: 'all', labelKey: 'filters.allStatuses' },
  { key: 'completed', labelKey: 'sessionList.completed' },
  { key: 'in_progress', labelKey: 'sessionList.inProgress' },
];

export function SessionFilters({
  filters,
  availableWards,
  activeFilterCount,
  onFilterChange,
  onClearFilters,
}: SessionFiltersProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const isCustomDateRange =
    !filters.datePreset || filters.datePreset === 'all';
  const showCustomDates =
    isCustomDateRange && (filters.dateFrom !== null || filters.dateTo !== null);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => setOpen(!open)} size="small">
          <Badge badgeContent={activeFilterCount} color="primary">
            <FilterList />
          </Badge>
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpen(!open)}
        >
          {t('filters.toggle')}
        </Typography>
        {activeFilterCount > 0 && (
          <Button size="small" onClick={onClearFilters} startIcon={<Close />}>
            {t('filters.clearFilters')}
          </Button>
        )}
      </Box>

      <Collapse in={open}>
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          {/* Date Range */}
          <Typography variant="subtitle2" gutterBottom>
            {t('filters.dateRange')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {DATE_PRESETS.map((preset) => (
              <Chip
                key={preset.key}
                label={t(preset.labelKey)}
                clickable
                color={
                  filters.datePreset === preset.key && !showCustomDates
                    ? 'primary'
                    : 'default'
                }
                variant={
                  filters.datePreset === preset.key && !showCustomDates
                    ? 'filled'
                    : 'outlined'
                }
                onClick={() => onFilterChange('datePreset', preset.key)}
              />
            ))}
            <Chip
              label={t('filters.customRange')}
              clickable
              color={showCustomDates ? 'primary' : 'default'}
              variant={showCustomDates ? 'filled' : 'outlined'}
              onClick={() => {
                // Switch to custom mode: clear preset, set today as dateFrom
                onFilterChange('datePreset', 'all');
                if (!filters.dateFrom) {
                  onFilterChange(
                    'dateFrom',
                    new Date().toISOString().split('T')[0],
                  );
                }
              }}
            />
          </Box>

          {/* Custom Date Inputs */}
          {showCustomDates && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label={t('filters.dateFrom')}
                type="date"
                size="small"
                value={filters.dateFrom || ''}
                onChange={(e) =>
                  onFilterChange('dateFrom', e.target.value || null)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('filters.dateTo')}
                type="date"
                size="small"
                value={filters.dateTo || ''}
                onChange={(e) =>
                  onFilterChange('dateTo', e.target.value || null)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          )}

          {/* Ward Filter */}
          {availableWards.length > 1 && (
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('filters.ward')}</InputLabel>
              <Select
                value={filters.ward}
                label={t('filters.ward')}
                onChange={(e) => onFilterChange('ward', e.target.value)}
              >
                <MenuItem value="">{t('filters.allWards')}</MenuItem>
                {availableWards.map((ward) => (
                  <MenuItem key={ward} value={ward}>
                    {ward}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Status Filter */}
          <Typography variant="subtitle2" gutterBottom>
            {t('filters.status')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {STATUS_OPTIONS.map((opt) => (
              <Chip
                key={opt.key}
                label={t(opt.labelKey)}
                clickable
                color={filters.status === opt.key ? 'primary' : 'default'}
                variant={
                  filters.status === opt.key ? 'filled' : 'outlined'
                }
                onClick={() => onFilterChange('status', opt.key)}
              />
            ))}
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
